import fetchWithRetry from './fetchWithRetry';
import type RequestOptions from './RequestOptions';
import type MailboxRequestOptions from './MailboxRequestOptions';
import { getConfig, type ServiceRequestConfig } from './config';
import checkAndLogMailboxInfo from './checkAndLogMailboxInfo';
import { getApp } from 'owa-config';
import { debugErrorThatWillShowErrorPopupOnly, trace } from 'owa-trace';
import getUrlWithAddedQueryParameters from 'owa-url/lib/getUrlWithAddedQueryParameters';
import { checkMailboxInfoForDiagnosticData } from './utils/checkMailboxInfoForDiagnosticData';
import { isServiceRequestSupportedForMailbox } from './utils/isServiceRequestSupportedForMailbox';
import { disallowedLicensingCallCheck } from './utils/disallowedLicensingCallCheck';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getOwaNetCoreEndpoint } from 'owa-netcore-flight/lib/getOwaNetCoreEndpoint';
import { isOwaNetCoreApiEnabled } from 'owa-netcore-flight/lib/isOwaNetCoreApiEnabled';
import { shouldCompareNetCoreResponse } from 'owa-netcore-flight/lib/shouldCompareNetCoreResponse';
import { shouldShadowCallNetCore } from 'owa-netcore-flight/lib/shouldShadowCallNetCore';
import { owaNetCoreComparator } from 'owa-netcore-flight/lib/owaNetCoreComparator';
import { addNetCoreAuthHeader } from 'owa-netcore-flight/lib/addNetCoreAuthHeader';
import { shouldNetCoreFallback } from 'owa-netcore-flight/lib/shouldNetCoreFallback';
import { logStartUsage } from 'owa-analytics-start';
import { makeServiceRequestB2 } from './B2ServiceUtils';
import { getB2ServiceApiMode } from 'owa-b2-flight/lib/getB2ServiceApiMode';
import { type B2ServiceApiMode } from 'owa-b2-flight/lib/B2ServiceApiMode';

export function makeServiceRequest<T>(
    actionName: string,
    parameters: any,
    options?: RequestOptions | MailboxRequestOptions
): Promise<T> {
    trace.info(`ServiceAction ${actionName}`, 'service');

    checkMailboxInfoForDiagnosticData(options?.mailboxInfo);
    if (!isServiceRequestSupportedForMailbox(options?.mailboxInfo)) {
        const msg = `Cannot issue OWS request for a mailbox: ${options?.mailboxInfo?.type} that does not support service requests`;
        debugErrorThatWillShowErrorPopupOnly(msg);
        throw new Error(
            'Cannot issue OWS request for a mailbox that does not support service requests'
        );
    }

    // Check to make sure this is not a disallowed licensing call
    disallowedLicensingCallCheck(options?.mailboxInfo, actionName);

    if (options?.mailboxInfo?.isRemoved) {
        debugErrorThatWillShowErrorPopupOnly('InvalidRequest: MailboxInfo was removed');
        throw new Error('InvalidRequest: MailboxInfo was removed');
    }

    const config = getConfig();
    checkAndLogMailboxInfo(config, 'Acct-MakeServiceRequestMailbox', options?.mailboxInfo);

    // Add TenantId to header for IIS log routing
    const tenantId = config.getTenantId(options?.mailboxInfo);
    if (tenantId) {
        options = options || ({} as RequestOptions);

        // Convert plain object to Headers instance if necessary
        if (!(options.headers instanceof Headers)) {
            const existingHeaders = options.headers || {};
            options.headers = new Headers(existingHeaders);
        }

        options.headers.set('X-TenantId', tenantId);
    }

    let endpoint;
    if (options?.endpoint) {
        endpoint = options.endpoint;
    } else {
        const baseUrl = options?.customBaseUrl
            ? options.customBaseUrlSubPath
                ? options.customBaseUrl.concat(options.customBaseUrlSubPath)
                : options.customBaseUrl
            : options?.customBaseUrlSubPath
            ? config.baseUrl.concat('/' + options.customBaseUrlSubPath)
            : config.baseUrl;
        endpoint = `${baseUrl}/service.svc?action=${actionName}`;
    }

    if (
        (config.isUserIdle !== undefined && config.isUserIdle()) ||
        options?.isUserActivity == false
    ) {
        endpoint = getUrlWithAddedQueryParameters(endpoint, { UA: '0' });
    }

    endpoint = getUrlWithAddedQueryParameters(
        endpoint,
        { app: getApp() },
        true /* ignore if present */
    );

    const b2ServiceModeForAction = getB2ServiceApiMode(
        actionName,
        config.getApplicationSettings,
        options?.mailboxInfo
    );

    // If B2 service is enabled for this action, route the request to B2 service
    if (b2ServiceModeForAction !== 0) {
        return makeServiceRequestB2<T>(
            actionName,
            endpoint,
            config,
            parameters,
            b2ServiceModeForAction,
            options
        );
    }

    return makeServiceRequestA<T>(actionName, endpoint, config, parameters, options);
}

export function makeServiceRequestA<T>(
    actionName: string,
    endpoint: string,
    config: ServiceRequestConfig,
    parameters: any,
    options?: RequestOptions | MailboxRequestOptions
): Promise<T> {
    // Check if NetCore is enabled for this action
    if (isOwaNetCoreApiEnabled(actionName, config.getApplicationSettings, options?.mailboxInfo)) {
        if (Object.keys(owaNetCoreComparator).includes(actionName)) {
            if (shouldCompareNetCoreResponse(config.getApplicationSettings, options?.mailboxInfo)) {
                return fetchComparison(
                    actionName,
                    endpoint,
                    config,
                    options,
                    parameters,
                    owaNetCoreComparator[actionName]
                );
            } else if (
                shouldShadowCallNetCore(config.getApplicationSettings, options?.mailboxInfo)
            ) {
                fetchNetCore(
                    actionName,
                    endpoint,
                    config,
                    options,
                    parameters,
                    false /* fallback */
                ).catch(() => {
                    // Note: Service requests may not have mailboxInfo for infrastructure/bootstrap calls.
                    logStartUsage('NetCoreShadowFailed', options?.mailboxInfo, { actionName });
                });
                return fetchWithRetry(actionName, endpoint, 1, options, parameters);
            }
        }
        return fetchNetCore(
            actionName,
            endpoint,
            config,
            options,
            parameters,
            shouldNetCoreFallback(config.getApplicationSettings, options?.mailboxInfo)
        );
    }
    return fetchWithRetry(actionName, endpoint, 1, options, parameters);
}

function fetchNetCore(
    actionName: string,
    endpoint: string,
    config: ServiceRequestConfig,
    options?: RequestOptions | MailboxRequestOptions,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    parameters?: any,
    fallback?: boolean
) {
    const netcoreEndpoint = getOwaNetCoreEndpoint(endpoint);
    const wantJsonResponse =
        !options?.returnResponseHeaders && !options?.returnFullResponseOnSuccess;
    const netcoreOptions: RequestOptions | MailboxRequestOptions = {
        ...options,
        returnResponseHeaders: true,
    };
    netcoreOptions.headers ??= new Headers();
    addNetCoreAuthHeader(
        netcoreOptions.headers as Headers,
        config.getApplicationSettings,
        netcoreOptions.mailboxInfo
    );
    // We know this is a owanetcore request so add x-responseorigin OwaNetCore in case we get a 404 or for whatever reason x-responseorigin isn't sent in the service response header.
    netcoreOptions.datapoint ??= {};
    netcoreOptions.datapoint.customData ??= {};
    netcoreOptions.datapoint.customData.responseOrigin = 'OwaNetCore';
    return fetchWithRetry(actionName, netcoreEndpoint, 1, netcoreOptions, parameters)
        .then(response => {
            if (
                fallback &&
                typeof response.status === 'number' &&
                !isSuccessStatusCode(response.status)
            ) {
                throw new Error('owanetcore response did not succeed');
            }
            if (wantJsonResponse) {
                return response.json();
            }
            return Promise.resolve(response);
        })
        .then(response => response)
        .catch(error => {
            if (fallback) {
                // Note: Service requests may not have mailboxInfo for infrastructure/bootstrap calls.
                logStartUsage('NetCoreFallback', options?.mailboxInfo, { actionName });
                const fallbackOptions: RequestOptions | MailboxRequestOptions = { ...options };
                fallbackOptions.datapoint ??= {};
                fallbackOptions.datapoint.customData ??= {};
                fallbackOptions.datapoint.customData.OwaNetCoreFallback = true;
                return fetchWithRetry(actionName, endpoint, 1, fallbackOptions, parameters);
            }
            throw error;
        });
}

function fetchComparison(
    actionName: string,
    endpoint: string,
    config: ServiceRequestConfig,
    options?: RequestOptions | MailboxRequestOptions,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    parameters?: any,
    compareCallback?: (owaResponse: any, netcoreResponse: any, actionName: string) => any
) {
    const owaRequestPromise = fetchWithRetry(actionName, endpoint, 1, options, parameters);
    const netcoreRequestPromise = fetchNetCore(
        actionName,
        endpoint,
        config,
        options,
        parameters,
        false /* fallback */
    );
    return Promise.allSettled([owaRequestPromise, netcoreRequestPromise]).then(results => {
        const [owaResult, netcoreResult] = results;
        if (owaResult.status == 'rejected') {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logStartUsage('NetCoreDiff', undefined, { actionName, diff: 'OWAFailed' });
            return owaResult.reason;
        }
        if (netcoreResult.status == 'rejected') {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logStartUsage('NetCoreDiff', undefined, { actionName, diff: 'NetCoreFailed' });
            return owaResult.value;
        }
        return (
            compareCallback?.(owaResult.value, netcoreResult.value, actionName) ?? owaResult.value
        );
    });
}
